import { Close } from '@carbon/icons-react';
import { Tag as AntTag } from 'antd';
import { TagProps as AntTagProps } from 'antd/es/tag';
import { FC, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { Color } from '../../styles';
import { CenterAlignedBox } from '../layout/layout';

export type TagProps = Omit<AntTagProps, 'color'> & {
  color?: Color;
  hoverColor?: Color;
  showSameColor?: boolean;
  aiGenerated?: boolean;
};

const DefaultTag: FC<React.PropsWithChildren<TagProps>> = ({
  color,
  ...rest
}) => <AntTag color="default" {...rest} closeIcon={<Close size={16} />} />;

const AiLabel = styled(CenterAlignedBox).attrs({ gap: 4 })``;
const AiMarker = styled.div`
  width: 4px;
  height: 4px;
`;

interface BaseTagProps extends TagProps {
  showSameColor?: boolean;
}

export const BaseTag = styled(DefaultTag)<BaseTagProps>`
  --tag-border-color: var(--tag-surface-color);

  border-radius: var(--border-radius-default);
  margin: 0;
  padding: calc(var(--spacing-02) - 2px) calc(var(--spacing-03) - 2px);
  /* border: 2px solid var(--tag-border-color); */
  border: 2px solid
    ${({ showSameColor }) =>
      showSameColor ? '#edf5ff' : 'var(--tag-surface-color)'};
  /* background: var(--tag-surface-color); */
  background: ${({ showSameColor }) =>
    showSameColor ? '#edf5ff' : 'var(--tag-surface-color)'};
  /* background: #0043ce; */
  /* color: var(--tag-color); */
  color: ${({ showSameColor }) =>
    showSameColor ? '#0043ce' : 'var(--tag-color)'};
  font-size: var(--font-size-sm);
  line-height: var(--line-height-tag);
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-02);
  ${AiMarker} {
    background-color: var(--tag-color);
  }
  ${AiLabel} {
    margin-left: 4px;
  }

  &:focus {
    --tag-border-color: var(--blue-60);
  }

  &:hover {
    opacity: 100%;
    --tag-surface-color: ${({ hoverColor, color }) =>
      hoverColor != null
        ? `var(--ozone-${hoverColor}-surface)`
        : `var(--ozone-${color}-surface)`};
    --tag-color: ${({ hoverColor, color }) =>
      hoverColor != null
        ? `var(--ozone-${hoverColor})`
        : `var(--ozone-${color})`};
  }

  &.disabled,
  &[disabled] {
    --tag-surface-color: var(--gray-10);
    --tag-color: var(--gray-30);
  }

  ${({ color }) => css`
    --tag-surface-color: var(--ozone-${color}-surface);
    --tag-color: var(--ozone-${color});
  `};

  cursor: ${ifProp('onClick', 'pointer', 'initial')};

  .ant-tag-close-icon {
    display: flex;
    color: var(--tag-color);
    &:hover {
      border-radius: 50%;
      color: var(--red-60);
      background-color: var(--red-20);
    }
  }
`;

interface Props extends TagProps {
  className?: string;
}
export const Tag: FC<PropsWithChildren<Props>> = (props) => {
  const { children, aiGenerated } = props;

  return (
    <BaseTag {...props}>
      {children}{' '}
      {aiGenerated ? (
        <AiLabel>
          <AiMarker />
          AI
        </AiLabel>
      ) : null}
    </BaseTag>
  );
};
